import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { CompanyProfile, IdentityProfile } from '../types'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

export class ApiError extends Error {
  code?: string
  constructor(message: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

async function apiFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(buildUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new ApiError('Session expired')
    }
    const p = await res.json().catch(() => null)
    // Backend validation messages are already human-readable and self-contained
    // (e.g. "Differentiators cannot be empty") — no need to prefix the raw field
    // key, which would just re-add the technical noise the message avoids.
    const details = Array.isArray(p?.error?.details) ? p.error.details : null
    const detailMessage = details?.length
      ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
      : null
    const message = detailMessage || p?.error?.message || p?.message || res.statusText || 'Request failed'
    throw new ApiError(message, p?.error?.code)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export function getCompanyProfile(workspaceId: string): Promise<CompanyProfile> {
  return apiFetch('GET', `/api/workspaces/${workspaceId}/company-profile`)
}

export function upsertIdentity(workspaceId: string, input: IdentityProfile): Promise<CompanyProfile> {
  return apiFetch('PUT', `/api/workspaces/${workspaceId}/company-profile/identity`, input)
}

// Multipart, not JSON — separate from apiFetch above since it needs the
// browser to set its own Content-Type (with the multipart boundary), and the
// body is a File, not a JSON-serializable value. Nothing is saved by this
// call; the caller merges the response into its own draft state.
export async function extractFromDocument(workspaceId: string, file: File): Promise<IdentityProfile> {
  const token = Cookies.get('token')
  const formData = new FormData()
  formData.append('file', file)

  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/company-profile/extract`), {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new ApiError('Session expired')
    }
    const p = await res.json().catch(() => null)
    const details = Array.isArray(p?.error?.details) ? p.error.details : null
    const detailMessage = details?.length
      ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
      : null
    const message = detailMessage || p?.error?.message || p?.message || res.statusText || 'Request failed'
    throw new ApiError(message, p?.error?.code)
  }

  return res.json()
}
